import React from 'react';
import IntroProductModal from '@fff-web/fff-ui-shared/IntroProductModal';
import ButtonLink from '@fff-web/fff-ui-shared/ButtonLink';
import { BoxFull } from '@fff-web/fff-ui-shared/Icon';
import classNames from 'classnames';
import Choice from './Choice';
import styles from './YourChoices.module.scss';
import env from '../../utils/env';
import PREVIEW_CUSTOMIZE_SLUG from './constants';
import type { ProductChoice } from '../../utils/Data/customizeConnector';
import ChooseForMe from './ChooseForMe/ChooseForMe';
import { SEASONAL_PLAN_CODE } from '../../utils/planCodes';
import { useSharedContext } from '../../pages/GTBCustomize/ContextProviders/SharedContext';
import { ITEM_TYPE } from '../../pages/GTBCustomize/ContextProviders/PublicShopContext';
import EXPERIENCE_FLOW from '../../utils/constants/experienceFlow';
import { FormattedProducts } from '../../pages/GTBCustomize/ContextProviders/PublicShopContextTypes';

const ADDITIONAL_CHOICES = ['additionalChoice1', 'additionalChoice2'];

type Props = {
  choices: any[];
};

const YourChoices = ({ choices }: Props) => {
  const { planCode, experienceFlow } = useSharedContext();
  const isSeasonal = planCode && planCode === SEASONAL_PLAN_CODE;
  const isPublicShop = experienceFlow === EXPERIENCE_FLOW.publicShop;
  const isGTB = experienceFlow === EXPERIENCE_FLOW.gtb;
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleEditChoice = (questionId: number) => {
    window.location.assign(
      `https://${env.originUrl}${PREVIEW_CUSTOMIZE_SLUG}/?planCode=${planCode}&q=${questionId}`,
    );
  };

  const hasIntroProduct = React.useMemo(
    () => !!choices?.some((choice) => choice.type === 'INTRO'),
    [choices],
  );

  let choicesWithExtras = [...choices];

  if (choicesWithExtras.length && isGTB && isSeasonal) {
    choicesWithExtras = choicesWithExtras.concat(ADDITIONAL_CHOICES);
  }

  if (choices.length <= 0) return null;

  return (
    <section id="choices" className={styles.yourChoices}>
      <IntroProductModal
        modalIsOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
      />
      <h2 className={styles.yourChoicesTitle}>Your Cart</h2>
      {isPublicShop && hasIntroProduct && (
        <div className={styles.membership}>
          <strong>
            <BoxFull />
            &nbsp;FabFitFun Membership
          </strong>
          <span className={styles.memberPricing}>
            Continues at $59.99 per season.
            {' '}
            <ButtonLink
              type="black"
              label="Learn more"
              onClick={() => setIsModalOpen(true)}
            />
          </span>
          <span className={styles.memberPricingUnlocked}>
            Member pricing unlocked
          </span>
          <span className={styles.membershipDisclaimer}>
            (Box not included with initial purchase.)
          </span>
        </div>
      )}
      <div
        className={classNames(styles.choices)}
        role="list"
      >
        {choicesWithExtras.map(
          (
            choice: ProductChoice | FormattedProducts | string,
            index: number,
          ) => {
            const choiceKey = typeof choice === 'string'
              ? choice
              : `${choice.name}${index}`;
            return (
              <div key={choiceKey} className={styles.choiceContainer}>
                {typeof choice === 'string' ? (
                  <ChooseForMe />
                ) : (
                  <Choice
                    title={choice.name}
                    brandName={
                      'brandName' in choice ? choice.brandName : undefined
                    }
                    image={choice.image}
                    handleEditChoice={handleEditChoice}
                    itemId={index + 1}
                    soldOut={'soldOut' in choice ? choice.soldOut : undefined}
                    publicRefill={
                      'type' in choice ? choice.type === ITEM_TYPE.refill : false
                    }
                    cadenceType={
                      'cadence' in choice ? choice.cadence : undefined
                    }
                    quantity={'quantity' in choice ? choice.quantity ?? 1 : choice.primaryQuantity}
                  />
                )}
              </div>
            );
          },
        )}
      </div>
    </section>
  );
};

export default YourChoices;
